import React, { useState, useEffect } from "react";
import { Grid, Button, Box } from "@mui/material";
import axios from "axios";
import Sidebar from "../../components/Sidebar/Sidebar";
import Header from "../../components/Header/Header";
import BenefitsTable from "../../components/BenefitsTable/BenefitsTable";
import AddBenefitModal from "../../components/AddBenefitModal/AddBenefitModal";
import styles from "./BenefitsPageStyles";
import LoadingSpinner from "../../components/LoadingSpinner/LoadingSpinner";

interface Benefit {
  id: number;
  benefit_name: string;
  benefit_type: string;
  description: string;
  assigned_to: Array<{ id: string; codename: string; gender: string }>;
}

const BenefitsPage: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [benefits, setBenefits] = useState<Benefit[]>([]);
  const [loading, setLoading] = useState(true);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  useEffect(() => {
    fetchBenefits();
  }, []);

  const fetchBenefits = async () => {
    setLoading(true);
    try {
      const response = await axios.get<Benefit[]>(
        `${process.env.REACT_APP_BACKEND_URL}/benefits`
      );
      setBenefits(response.data);
    } catch (error) {
      console.error("Error fetching benefits:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddBenefit = async (
    newBenefit: Omit<Benefit, "id" | "assigned_to">
  ) => {
    try {
      const response = await axios.post<Benefit>(
        `${process.env.REACT_APP_BACKEND_URL}/benefits/`,
        newBenefit
      );
      setBenefits([...benefits, response.data]);
      handleClose();
    } catch (error) {
      console.error("Error adding benefit:", error);
    }
  };

  const handleAssignBenefit = async (
    benefitId: number | null,
    memberId: string
  ) => {
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/benefits/${benefitId}/recommend`,
        {
          memberId,
        }
      );
      fetchBenefits(); // Refresh the benefits list after assigning
    } catch (error) {
      console.error("Error assigning benefit:", error);
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <Grid container sx={styles.BenefitsContainer}>
      <Grid item xs={2.5}>
        <Sidebar />
      </Grid>
      <Grid item xs={9}>
        <Header />
        <Box sx={styles.BenefitsContent}>
          <Button
            variant="contained"
            onClick={handleOpen}
            sx={styles.addButton}
          >
            Add Benefit
          </Button>
          <BenefitsTable
            benefits={benefits}
            onRecommendBenefit={handleAssignBenefit}
          />
        </Box>
      </Grid>
      <AddBenefitModal
        open={open}
        handleClose={handleClose}
        onAddBenefit={handleAddBenefit}
      />
    </Grid>
  );
};

export default BenefitsPage;
